import { FC } from 'react'
import { Navigate } from 'react-router-dom'
import { useAppType } from '@/shared/contexts/AppTypeContext'
import { isLegacyApp } from '@/types/appType'

// legacy 側 portal page
import { PortalHomePage as LegacyPortalHomePage } from '@/apps/legacy/pages/portal/index'
import LegacyPatientsPage from '@/apps/legacy/pages/portal/patients'
import { PharmacistManagementPage as LegacyPharmacistsPage } from '@/apps/legacy/pages/portal/pharmacists'
import LegacySoapSettingsPage from '@/apps/legacy/pages/portal/soap-settings'
import { TextReplacementsPage as LegacyTextReplacementsPage } from '@/apps/legacy/pages/portal/text-replacements'
import LegacyLineSettingsPage from '@/apps/legacy/pages/portal/line-settings'
import LegacyFormatInsertSettingsPage from '@/apps/legacy/pages/portal/format-insert-settings'
import LegacyNSIPSExportPage from '@/apps/legacy/pages/portal/nsips-export'
import LegacySoapTemplateListPage from '@/apps/legacy/pages/portal/soaps/index'
import LegacyDrugDetailPage from '@/apps/legacy/pages/portal/soaps/drugs/[id]'
import LegacyDrugGroupDetailPage from '@/apps/legacy/pages/portal/soaps/groups/[id]'
import LegacyDrugGroupCreatePage from '@/apps/legacy/pages/portal/soaps/groups/create'
import LegacyDrugGroupDrugsPage from '@/apps/legacy/pages/portal/soaps/groups/[id]/drugs'
import { MetricsUsagePage as LegacyMetricsUsagePage } from '@/apps/legacy/pages/portal/metrics/usage/index'
import { GuidanceAnalysisPage as LegacyGuidanceAnalysisPage } from '@/apps/legacy/pages/portal/metrics/guidance'
import { MetricsRecordsPage as LegacyMetricsRecordsPage } from '@/apps/legacy/pages/portal/metrics/records/index'
import { MetricsDetailPage as LegacyMetricsDetailPage } from '@/apps/legacy/pages/portal/metrics/records/[id]'

// latest 側 portal page
import { PortalHomePage as LatestPortalHomePage } from '@/apps/latest/pages/portal/index'
import LatestPatientsPage from '@/apps/latest/pages/portal/patients'
import { PharmacistManagementPage as LatestPharmacistsPage } from '@/apps/latest/pages/portal/pharmacists'
import LatestSoapSettingsPage from '@/apps/latest/pages/portal/soap-settings'
import LatestDisplaySettingsPage from '@/apps/latest/pages/portal/display-settings'
import { TutorialPage as LatestTutorialPage } from '@/apps/latest/pages/portal/tutorial'
import { TextReplacementsPage as LatestTextReplacementsPage } from '@/apps/latest/pages/portal/text-replacements'
import LatestLineSettingsPage from '@/apps/latest/pages/portal/line-settings'
import LatestFormatInsertSettingsPage from '@/apps/latest/pages/portal/format-insert-settings'
import LatestNSIPSExportPage from '@/apps/latest/pages/portal/nsips-export'
import LatestSoapTemplateListPage from '@/apps/latest/pages/portal/soaps/index'
import LatestDrugGroupDetailPage from '@/apps/latest/pages/portal/soaps/groups/[id]'
import LatestDrugGroupDrugsPage from '@/apps/latest/pages/portal/soaps/groups/[id]/drugs'
import LatestSoapRulesPage from '@/apps/latest/pages/portal/soap-rules'
import LatestTracingReportsPage from '@/apps/latest/pages/portal/tracing-reports'
import LatestSoapMastersPage from '@/apps/latest/pages/portal/soap-masters'
import { MetricsUsagePage as LatestMetricsUsagePage } from '@/apps/latest/pages/portal/metrics/usage/index'
import { GuidanceAnalysisPage as LatestGuidanceAnalysisPage } from '@/apps/latest/pages/portal/metrics/guidance'
import { MetricsRecordsPage as LatestMetricsRecordsPage } from '@/apps/latest/pages/portal/metrics/records/index'
import { MetricsDetailPage as LatestMetricsDetailPage } from '@/apps/latest/pages/portal/metrics/records/[id]'
import { MetricsPage as LatestMetricsPage } from '@/apps/latest/pages/portal/metrics/index'
import { KasanAnalysisPage as LatestKasanAnalysisPage } from '@/apps/latest/pages/portal/metrics/kasan/index'

// factory: legacy/latest を appType で振り分ける
const make = (Legacy: FC, Latest: FC): FC => {
  const Dispatcher: FC = () => {
    const { appType } = useAppType()
    return isLegacyApp(appType) ? <Legacy /> : <Latest />
  }
  return Dispatcher
}

export const PortalHomeDispatcher = make(
  LegacyPortalHomePage,
  LatestPortalHomePage
)
export const PortalPatientsDispatcher = make(
  LegacyPatientsPage,
  LatestPatientsPage
)
export const PortalPharmacistsDispatcher = make(
  LegacyPharmacistsPage,
  LatestPharmacistsPage
)
export const PortalSoapSettingsDispatcher = make(
  LegacySoapSettingsPage,
  LatestSoapSettingsPage
)
// 表示設定は latest 専用の新規画面 (legacy 版なし) のため両 appType で latest を描画。
export const PortalDisplaySettingsDispatcher = make(
  LatestDisplaySettingsPage,
  LatestDisplaySettingsPage
)
// チュートリアル (検証患者作成) は latest 専用の新規画面 (legacy 版なし)。
export const PortalTutorialDispatcher = make(
  LatestTutorialPage,
  LatestTutorialPage
)
// SOAPルール / トレーシングレポート / 定型文 は latest 専用の新規画面 (legacy 版なし)。
export const PortalSoapRulesDispatcher = make(
  LatestSoapRulesPage,
  LatestSoapRulesPage
)
export const PortalSoapMastersDispatcher = make(
  LatestSoapMastersPage,
  LatestSoapMastersPage
)
export const PortalTracingReportsDispatcher = make(
  LatestTracingReportsPage,
  LatestTracingReportsPage
)
export const PortalTextReplacementsDispatcher = make(
  LegacyTextReplacementsPage,
  LatestTextReplacementsPage
)
export const PortalLineSettingsDispatcher = make(
  LegacyLineSettingsPage,
  LatestLineSettingsPage
)
export const PortalFormatInsertSettingsDispatcher = make(
  LegacyFormatInsertSettingsPage,
  LatestFormatInsertSettingsPage
)
export const PortalNSIPSExportDispatcher = make(
  LegacyNSIPSExportPage,
  LatestNSIPSExportPage
)
export const PortalSoapTemplateListDispatcher = make(
  LegacySoapTemplateListPage,
  LatestSoapTemplateListPage
)
// latest では個別薬剤テンプレ編集・作成ページを撤去済み (ADR-0017)。
// route は legacy 用に残し、latest 側はグループ一覧へ逃がす。
const RedirectToDrugGroups: FC = () => <Navigate to="/portal/soaps" replace />
export const PortalDrugDetailDispatcher = make(
  LegacyDrugDetailPage,
  RedirectToDrugGroups
)
export const PortalDrugGroupDetailDispatcher = make(
  LegacyDrugGroupDetailPage,
  LatestDrugGroupDetailPage
)
export const PortalDrugGroupCreateDispatcher = make(
  LegacyDrugGroupCreatePage,
  RedirectToDrugGroups
)
export const PortalDrugGroupDrugsDispatcher = make(
  LegacyDrugGroupDrugsPage,
  LatestDrugGroupDrugsPage
)
// 統計ページ (latest 専用の統合ページ。legacy は旧・利用統計を表示)
export const PortalMetricsDispatcher = make(
  LegacyMetricsUsagePage,
  LatestMetricsPage
)
// 算定分析 (latest 専用。soap-rules と同じく legacy 側にも latest 実装を出す)
export const PortalKasanAnalysisDispatcher = make(
  LatestKasanAnalysisPage,
  LatestKasanAnalysisPage
)
export const PortalMetricsUsageDispatcher = make(
  LegacyMetricsUsagePage,
  LatestMetricsUsagePage
)
export const PortalGuidanceAnalysisDispatcher = make(
  LegacyGuidanceAnalysisPage,
  LatestGuidanceAnalysisPage
)
export const PortalMetricsRecordsDispatcher = make(
  LegacyMetricsRecordsPage,
  LatestMetricsRecordsPage
)
export const PortalMetricsDetailDispatcher = make(
  LegacyMetricsDetailPage,
  LatestMetricsDetailPage
)
